'use client';

import classNames from 'classnames';
import React, {type ComponentProps, useCallback, useState, useRef, type FocusEvent} from 'react';
import {type DateRange, DayPicker, type SelectRangeEventHandler} from 'react-day-picker';

import 'react-day-picker/dist/style.css';
import {PepIcon} from './PepIcon';
import {BodyText, SmallText} from './Text';
import {useUiStrings} from '#/features/store/uiStrings';
import dayjs from 'dayjs';

type Props = ComponentProps<'div'> & {
  range: DateRange | undefined;
  onRangeSelect: SelectRangeEventHandler;
};

const Calendar = ({className, range, onRangeSelect, ...props}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);

  const uiStrings = useUiStrings();

  const handleFocus = useCallback(() => {
    setOpen(true);
  }, []);

  const handleBlur = useCallback(({currentTarget, relatedTarget}: FocusEvent) => {
    if (!currentTarget.contains(relatedTarget)) {
      // Close if new focused element (relatedTarget) is not a child of this component
      setOpen(false);
    }
  }, []);

  const {to, from} = range ?? {};
  const rangeString = to && from ? `${dayjs(from).format('MMM DD')} - ${dayjs(to).format('MMM DD')}` : null;

  return (
    <div
      ref={ref}
      className={classNames('group relative select-none', className)}
      {...props}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div className="w-full cursor-pointer">
        <button
          aria-label={uiStrings.events.selectDate}
          className="flex w-full items-center justify-between rounded-full border border-midground3 px-4 py-2 text-left"
        >
          <BodyText>{rangeString ?? uiStrings.events.selectDate}</BodyText>
          <PepIcon iconName="ChevronDown" strokeColor="midground4" />
        </button>
      </div>
      {isOpen && (
        <div
          tabIndex={0}
          className="absolute -start-2 top-12 z-[10] rounded-2xl border border-midground3 bg-background px-4 py-2 mobile:-start-7 mobile:top-8 mobile:scale-90"
        >
          <button onClick={() => setOpen(false)} className="-mr-2 ml-auto block cursor-pointer p-2">
            <PepIcon strokeColor="foreground" iconName={'Remove'} />
          </button>
          <DayPicker
            mode="range"
            selected={range}
            onSelect={onRangeSelect}
            modifiersStyles={{
              range_start: {backgroundColor: 'var(--primary)', color: 'var(--background)'},
              range_middle: {backgroundColor: 'var(--primary)', color: 'var(--background)'},
              range_end: {backgroundColor: 'var(--primary)', color: 'var(--background)'},
            }}
            defaultMonth={range?.to}
            classNames={{
              button: 'rdp-button hover:!bg-primary/25',
              day_selected:
                'rdp-day_selected aria-selected:bg-primary aria-selected:text-background aria-selected:hover:text-dark',
              root: 'rdp text-foreground bg-background',
            }}
          />
          <SmallText color={'secondary'} className="mx-4 mb-2 hidden group-has-[.rdp-day:focus-visible]:block">
            Use arrow keys to traverse the calendar. Press enter to pick a date range
          </SmallText>
        </div>
      )}
    </div>
  );
};

export default Calendar;
